import { useState, useEffect, useRef } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { t } from '../i18n';
import { themeTokens as c } from '../themeTokens';

const DROPDOWN_MAX_HEIGHT = 200;

interface SearchableSelectProps {
  options: Record<string, string>;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  name?: string;
  error?: boolean;
  required?: boolean;
  smallText?: boolean;
  /** Show search input in dropdown (default true). Set false for short lists. */
  searchable?: boolean;
}

export function SearchableSelect({ options, value, onChange, placeholder, name, error, required, smallText = false, searchable = true }: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [hovered, setHovered] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number; openUpward: boolean } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  let optionsList = Object.entries(options).map(([val, label]) => ({ value: val, label }));
  const allNumeric = optionsList.every((opt) => /^\d+$/.test(opt.value));
  if (allNumeric) {
    optionsList = optionsList.slice().sort((a, b) => Number(a.value) - Number(b.value));
  }
  const filteredOptions = searchable
    ? optionsList.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase()))
    : optionsList;

  const selectedLabel = options[value] || '';

  useEffect(() => {
    if (!isOpen || !wrapperRef.current) {
      setDropdownPosition(null);
      return;
    }
    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
    setDropdownPosition({
      left: rect.left,
      top: rect.bottom,
      width: rect.width,
      openUpward,
    });
    return () => setDropdownPosition(null);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        wrapperRef.current && !wrapperRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleSelect = (val: string) => {
    onChange(val);
    setIsOpen(false);
    setSearch('');
  };

  const dropdownContent = isOpen && dropdownPosition && (
    <div
      ref={dropdownRef}
      class="rounded shadow"
      style={{
        position: 'fixed',
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        ...(dropdownPosition.openUpward
          ? { bottom: window.innerHeight - dropdownPosition.top + 4 }
          : { top: dropdownPosition.top + 4 }),
        zIndex: 10050,
        maxHeight: `${DROPDOWN_MAX_HEIGHT}px`,
        overflowY: 'auto',
        overflowX: 'hidden',
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: c.radius,
      }}
    >
      {searchable && (
        <div class="sticky-top border-bottom" style={{ background: c.bg, borderColor: c.border, boxSizing: 'border-box', overflow: 'hidden', padding: '0.5rem 1rem' }}>
          <input
            ref={inputRef}
            type="text"
            placeholder={t('select.search')}
            value={search}
            onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            onClick={(e) => e.stopPropagation()}
            style={{
              minHeight: '36px',
              width: '100%',
              boxSizing: 'border-box',
              padding: '0.375rem 0.75rem',
              border: `1px solid ${c.border}`,
              borderRadius: c.radius,
              fontSize: 'var(--sw-font-size-sm,var(--sl-font-size-sm,0.875em))',
            }}
          />
        </div>
      )}
      <ul class="mb-0" style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((opt) => {
            const isSelected = opt.value === value;
            const isHover = hovered === opt.value && !isSelected;
            return (
              <li
                key={opt.value}
                style={{
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                  minHeight: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  background: isSelected ? c.primary : isHover ? c.hover : undefined,
                  color: isSelected ? '#fff' : undefined,
                }}
                onMouseEnter={() => setHovered(opt.value)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => handleSelect(opt.value)}
              >
                {opt.label}
              </li>
            );
          })
        ) : (
          <li style={{ padding: '0.5rem 1rem', minHeight: '36px', color: c.muted, fontSize: 'var(--sw-font-size-sm,var(--sl-font-size-sm,0.875em))', textAlign: 'center' }}>{t('select.noResults')}</li>
        )}
      </ul>
    </div>
  );

  return (
    <div class="position-relative" ref={wrapperRef} style={{ width: '100%' }}>
      <div
        class={`form-select ${error ? 'is-invalid' : ''} ${smallText ? 'sw-select-small' : ''} ${!value ? 'text-muted sw-select-placeholder' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          width: '100%',
          boxSizing: 'border-box',
          fontSize: (!value || smallText) ? 'var(--sw-font-size-sm,var(--sl-font-size-sm,0.875em))' : undefined,
          lineHeight: (!value || smallText) ? 'var(--sw-line-height,var(--sl-line-height,1.5))' : undefined,
        }}
      >
        {selectedLabel || placeholder || t('select.placeholder')}
      </div>
      <input type="hidden" name={name} value={value} required={required} />
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
}
